import { motion } from "framer-motion";
import Navigation from "../../components/Navigation";
import PageHero from "../../components/PageHero";

// Animation constants - extracted to prevent inline object recreation
const FADE_IN_UP = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const SLIDE_IN_DELAYS = {
  first: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.1 } },
  second: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.2 } },
  third: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.3 } },
  fourth: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.4 } }
};

// Font style constant
const PLAYFAIR_STYLE = { fontFamily: "'Playfair Display', serif" };

const FinancialSupportPage = () => {
  return (
    <div data-testid="financial-support-page" className="min-h-screen bg-[#FAFAFA]">
      <Navigation />
      
      <PageHero 
        image="https://customer-assets-7cd3h4nn.emergentagent.net/job_4bb091c1-e4be-474e-9067-19b51e555ba6/artifacts/0mdb2rzq_PXL_20260414_141047472~3.webp"
        alt="Financial Support - Murphy's Giving Market"
        objectPosition="top"
      />

      {/* Financial Support Content */}
      <section className="py-12 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={FADE_IN_UP.initial}
            animate={FADE_IN_UP.animate}
            transition={FADE_IN_UP.transition}
          >
            <h1 
              data-testid="financial-support-title"
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-8"
              style={PLAYFAIR_STYLE}
            >
              Financial Support
            </h1>
            
            {/* Tax-exempt statement */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
              <p className="text-lg text-[#333333] leading-relaxed">
                Murphy's Giving Market is a 501c3 tax-exempt, publicly supported organization (EIN 85-3176958). Your donation is tax-deductible to the fullest extent allowed by law.
              </p>
            </div>

            {/* Donation Methods */}
            <div className="space-y-8">
              {/* Donate by Check */}
              <motion.div
                initial={SLIDE_IN_DELAYS.first.initial}
                animate={SLIDE_IN_DELAYS.first.animate}
                transition={SLIDE_IN_DELAYS.first.transition}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8"
              >
                <h2 
                  className="text-2xl font-bold text-[#C41E3A] mb-4"
                  style={PLAYFAIR_STYLE}
                >
                  Donate by Check
                </h2>
                <p className="text-[#333333] mb-4">Mail your check to:</p>
                <address className="text-[#333333] not-italic pl-4 border-l-4 border-[#C41E3A]">
                  <strong>Murphy's Giving Market</strong><br />
                  7408 West Chester Pike<br />
                  Upper Darby, PA 19082
                </address>
              </motion.div>

              {/* Donate Online */}
              <motion.div
                initial={SLIDE_IN_DELAYS.second.initial}
                animate={SLIDE_IN_DELAYS.second.animate}
                transition={SLIDE_IN_DELAYS.second.transition}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8"
              >
                <h2 
                  className="text-2xl font-bold text-[#C41E3A] mb-4"
                  style={PLAYFAIR_STYLE}
                >
                  Donate Online
                </h2>
                <p className="text-[#333333] mb-6">
                  Make a secure online donation through Zeffy. Zeffy is a free fundraising platform supported entirely by optional donor tips, so Murphy's Giving Market receives 100% of your contribution. You can adjust the tip amount before completing your donation.
                </p>
                <a
                  href="https://www.zeffy.com/en-US/donation-form/d4b70ccf-7c7a-43d8-a48e-1328d3056f49"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="donate-online-btn"
                  className="inline-block bg-[#C41E3A] hover:bg-[#a31830] text-white font-semibold px-8 py-3 rounded-full transition-all duration-200 hover:scale-105"
                >
                  Donate via Zeffy
                </a>
              </motion.div>

              {/* Donate through DAF */}
              <motion.div
                initial={SLIDE_IN_DELAYS.third.initial}
                animate={SLIDE_IN_DELAYS.third.animate}
                transition={SLIDE_IN_DELAYS.third.transition}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8"
              >
                <h2 
                  className="text-2xl font-bold text-[#C41E3A] mb-4"
                  style={PLAYFAIR_STYLE}
                >
                  Donate through a Donor-Advised Fund
                </h2>
                <p className="text-[#333333]">
                  Recommend a grant to Murphy's Giving Market through your Donor-Advised Fund. When making your recommendation, search for Murphy's Giving Market and use our EIN (85-3176958). Contact us if you need additional information to complete your DAF grant.
                </p>
              </motion.div>

              {/* Donate Stock */}
              <motion.div
                initial={SLIDE_IN_DELAYS.fourth.initial}
                animate={SLIDE_IN_DELAYS.fourth.animate}
                transition={SLIDE_IN_DELAYS.fourth.transition}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8"
              >
                <h2 
                  className="text-2xl font-bold text-[#C41E3A] mb-4"
                  style={PLAYFAIR_STYLE}
                >
                  Donate Stock
                </h2>
                <p className="text-[#333333]">
                  You can make a gift of stock to Murphy's Giving Market through <a href="https://delcofoundation.org/" target="_blank" rel="noopener noreferrer" className="text-[#C41E3A] hover:underline">The Foundation for Delaware County</a>. Please contact us to help you complete your gift. You will need to provide certain information to your broker and to the Foundation to ensure that your donation reaches the Murphy's Giving Market Fund. You will be credited with the average of the high and low selling price of the stock on the day it reaches the Foundation account.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FinancialSupportPage;
